/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BaseHttpRequest } from './core/BaseHttpRequest';
import type { OpenAPIConfig } from './core/OpenAPI';
import { AxiosHttpRequest } from './core/AxiosHttpRequest';

import { AlertService } from './services/AlertService';
import { ApiKeyService } from './services/ApiKeyService';
import { AuditLogService } from './services/AuditLogService';
import { BlockRulesService } from './services/BlockRulesService';
import { ClaudeTemporaryService } from './services/ClaudeTemporaryService';
import { ConfluenceService } from './services/ConfluenceService';
import { DashboardService } from './services/DashboardService';
import { EventService } from './services/EventService';
import { ForMagiAuthService } from './services/ForMagiAuthService';
import { ForMagiCallbackService } from './services/ForMagiCallbackService';
import { ForMagiTranscribeRetryService } from './services/ForMagiTranscribeRetryService';
import { GoogleCalendarService } from './services/GoogleCalendarService';
import { IntegrationService } from './services/IntegrationService';
import { LabelService } from './services/LabelService';
import { LicenseService } from './services/LicenseService';
import { LinearService } from './services/LinearService';
import { MagicLinkService } from './services/MagicLinkService';
import { MembersService } from './services/MembersService';
import { MsTeamsService } from './services/MsTeamsService';
import { NotificationService } from './services/NotificationService';
import { NotionService } from './services/NotionService';
import { OutlookCalendarService } from './services/OutlookCalendarService';
import { PipedriveService } from './services/PipedriveService';
import { QuotaService } from './services/QuotaService';
import { RecordService } from './services/RecordService';
import { SalesforceService } from './services/SalesforceService';
import { SamlService } from './services/SamlService';
import { ScimService } from './services/ScimService';
import { ServiceService } from './services/ServiceService';
import { ShareRecordDetailService } from './services/ShareRecordDetailService';
import { SlackBotService } from './services/SlackBotService';
import { SlackChannelService } from './services/SlackChannelService';
import { SlackUnfurlService } from './services/SlackUnfurlService';
import { TeamService } from './services/TeamService';
import { TeamIntegrationService } from './services/TeamIntegrationService';
import { TeamRecordService } from './services/TeamRecordService';
import { UsageService } from './services/UsageService';
import { UserService } from './services/UserService';
import { WebhookService } from './services/WebhookService';
import { WorkspaceService } from './services/WorkspaceService';
import { WorkspaceCardService } from './services/WorkspaceCardService';
import { WorkspaceInviteService } from './services/WorkspaceInviteService';
import { WorkspaceRestrictedEventWordService } from './services/WorkspaceRestrictedEventWordService';
import { WorkspaceRolesService } from './services/WorkspaceRolesService';
import { ZapierService } from './services/ZapierService';

type HttpRequestConstructor = new (config: OpenAPIConfig) => BaseHttpRequest;

export class BackendApiClient {
  public readonly alert: AlertService;
  public readonly apiKey: ApiKeyService;
  public readonly auditLog: AuditLogService;
  public readonly blockRules: BlockRulesService;
  public readonly claudeTemporary: ClaudeTemporaryService;
  public readonly confluence: ConfluenceService;
  public readonly dashboard: DashboardService;
  public readonly event: EventService;
  public readonly forMagiAuth: ForMagiAuthService;
  public readonly forMagiCallback: ForMagiCallbackService;
  public readonly forMagiTranscribeRetry: ForMagiTranscribeRetryService;
  public readonly googleCalendar: GoogleCalendarService;
  public readonly integration: IntegrationService;
  public readonly label: LabelService;
  public readonly license: LicenseService;
  public readonly linear: LinearService;
  public readonly magicLink: MagicLinkService;
  public readonly members: MembersService;
  public readonly msTeams: MsTeamsService;
  public readonly notification: NotificationService;
  public readonly notion: NotionService;
  public readonly outlookCalendar: OutlookCalendarService;
  public readonly pipedrive: PipedriveService;
  public readonly quota: QuotaService;
  public readonly record: RecordService;
  public readonly salesforce: SalesforceService;
  public readonly saml: SamlService;
  public readonly scim: ScimService;
  public readonly service: ServiceService;
  public readonly shareRecordDetail: ShareRecordDetailService;
  public readonly slackBot: SlackBotService;
  public readonly slackChannel: SlackChannelService;
  public readonly slackUnfurl: SlackUnfurlService;
  public readonly team: TeamService;
  public readonly teamIntegration: TeamIntegrationService;
  public readonly teamRecord: TeamRecordService;
  public readonly usage: UsageService;
  public readonly user: UserService;
  public readonly webhook: WebhookService;
  public readonly workspace: WorkspaceService;
  public readonly workspaceCard: WorkspaceCardService;
  public readonly workspaceInvite: WorkspaceInviteService;
  public readonly workspaceRestrictedEventWord: WorkspaceRestrictedEventWordService;
  public readonly workspaceRoles: WorkspaceRolesService;
  public readonly zapier: ZapierService;

  public readonly request: BaseHttpRequest;

  constructor(
    config?: Partial<OpenAPIConfig>,
    HttpRequest: HttpRequestConstructor = AxiosHttpRequest
  ) {
    this.request = new HttpRequest({
      BASE: config?.BASE ?? 'https://dev-api.callabo.ai',
      INSTANCE: config?.INSTANCE,
      VERSION: config?.VERSION ?? '0.1.0',
      WITH_CREDENTIALS: config?.WITH_CREDENTIALS ?? false,
      CREDENTIALS: config?.CREDENTIALS ?? 'include',
      TOKEN: config?.TOKEN,
      USERNAME: config?.USERNAME,
      PASSWORD: config?.PASSWORD,
      HEADERS: config?.HEADERS,
      ENCODE_PATH: config?.ENCODE_PATH,
    });

    this.alert = new AlertService(this.request);
    this.apiKey = new ApiKeyService(this.request);
    this.auditLog = new AuditLogService(this.request);
    this.blockRules = new BlockRulesService(this.request);
    this.claudeTemporary = new ClaudeTemporaryService(this.request);
    this.confluence = new ConfluenceService(this.request);
    this.dashboard = new DashboardService(this.request);
    this.event = new EventService(this.request);
    this.forMagiAuth = new ForMagiAuthService(this.request);
    this.forMagiCallback = new ForMagiCallbackService(this.request);
    this.forMagiTranscribeRetry = new ForMagiTranscribeRetryService(this.request);
    this.googleCalendar = new GoogleCalendarService(this.request);
    this.integration = new IntegrationService(this.request);
    this.label = new LabelService(this.request);
    this.license = new LicenseService(this.request);
    this.linear = new LinearService(this.request);
    this.magicLink = new MagicLinkService(this.request);
    this.members = new MembersService(this.request);
    this.msTeams = new MsTeamsService(this.request);
    this.notification = new NotificationService(this.request);
    this.notion = new NotionService(this.request);
    this.outlookCalendar = new OutlookCalendarService(this.request);
    this.pipedrive = new PipedriveService(this.request);
    this.quota = new QuotaService(this.request);
    this.record = new RecordService(this.request);
    this.salesforce = new SalesforceService(this.request);
    this.saml = new SamlService(this.request);
    this.scim = new ScimService(this.request);
    this.service = new ServiceService(this.request);
    this.shareRecordDetail = new ShareRecordDetailService(this.request);
    this.slackBot = new SlackBotService(this.request);
    this.slackChannel = new SlackChannelService(this.request);
    this.slackUnfurl = new SlackUnfurlService(this.request);
    this.team = new TeamService(this.request);
    this.teamIntegration = new TeamIntegrationService(this.request);
    this.teamRecord = new TeamRecordService(this.request);
    this.usage = new UsageService(this.request);
    this.user = new UserService(this.request);
    this.webhook = new WebhookService(this.request);
    this.workspace = new WorkspaceService(this.request);
    this.workspaceCard = new WorkspaceCardService(this.request);
    this.workspaceInvite = new WorkspaceInviteService(this.request);
    this.workspaceRestrictedEventWord = new WorkspaceRestrictedEventWordService(this.request);
    this.workspaceRoles = new WorkspaceRolesService(this.request);
    this.zapier = new ZapierService(this.request);
  }
}
